import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EmptyState } from '@/components/molecules/EmptyState';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import { useCageName } from '@/contexts/cage';
import type { ImageFeedback } from '@/domain/imageFeedback';
import type { ImageState } from '@/hooks/image-state';

import styles from './CageImagePageContent.module.scss';

export interface CageImagePageContentProps {
    detectionImageState: ImageState;
    healthType?: string;
    isOptoScaleAdmin?: boolean;

    onCommitFalseDetection: (feedbackDto: ImageFeedback) => void;
}

export default function CageImagePageContent(props: CageImagePageContentProps) {
    const {
        detectionImageState,
        isOptoScaleAdmin = false,
        onCommitFalseDetection = () => {},
    } = props;
    const { t } = useTranslation();
    const cageName = useCageName();
    const isLoading = detectionImageState.loading;
    const hasNoImages = detectionImageState.dayswithimages.length === 0 && !isLoading;

    return (
        <PageLayout title={cageName} subtitle={t('cageDashboard.cageImages.images')}>
            <div className={styles.pageBoxLayout}>
                <div>
                    <ContentBlock size="compact" spacing="none">
                        {isLoading ? (
                            <Spinner size="lg" />
                        ) : hasNoImages ? (
                            <EmptyState title={t('common.labels.atTheMomentWeDo')} />
                        ) : (
                            <ImageViewer
                                key="catchoftheday"
                                variant="catchOfTheDay"
                                isOptoScaleAdmin={isOptoScaleAdmin}
                                onCommitImageFeedback={onCommitFalseDetection}
                                images={detectionImageState.images}
                                daysWithImages={detectionImageState.dayswithimages}
                                onDayChange={detectionImageState.onAfterTimelineChangeHandler}
                                initialImageIndex={detectionImageState.initialImageIndex}
                                isFetching={detectionImageState.isFetching}
                            />
                        )}
                    </ContentBlock>
                </div>
                <div>
                    <ContentBlock
                        title={t('cageDashboard.common.todaysCatch')}
                        subtitle={t('cageDashboard.cageImages.aRandomSelectionOfTodays')}
                    />
                </div>
            </div>
        </PageLayout>
    );
}
